import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown, ShieldCheck, Server, Lock } from "lucide-react";
import { useLang } from "../i18n";
import { buildWhatsAppLink } from "../lib/constants";
import { scrollTo } from "./Header";
import { CountUp } from "./CountUp";

const MaskedLine = ({ children, delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.span
      className="block"
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 28 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, reduce ? 1.06 : 1.18]);
  const frameY = useTransform(scrollYProgress, [0, 1], ["0px", reduce ? "0px" : "-70px"]);

  const stats = [
    { end: 2019, prefix: lang === "pt" ? "Desde " : "Since ", suffix: "", l: t.hero.stat1label },
    { end: 24, suffix: "/7", l: t.hero.stat3label },
    { text: t.hero.endToEnd, l: t.hero.endToEndLabel },
  ];

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden noise-overlay"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img
          src={`${process.env.PUBLIC_URL}/images/hero-bg.jpg`}
          alt="Data center com servidores"
          className="h-full w-full object-cover object-center opacity-45 grayscale-[25%]"
          fetchPriority="high"
        />
      </motion.div>

      {/* Overlays */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#050507]/85 via-[#050507]/55 to-[#050507]" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#050507] via-[#050507]/30 to-transparent" />
      <div className="absolute inset-0 -z-10 grid-lines opacity-30" />
      <div className="absolute -top-40 -left-40 h-[520px] w-[520px] -z-10 blue-glow" />

      {/* Floating particles */}
      {!reduce &&
        [...Array(12)].map((_, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full bg-[#00e5ff]/40"
            style={{
              width: 2 + (i % 3),
              height: 2 + (i % 3),
              left: `${(i * 71) % 100}%`,
              top: `${(i * 43) % 100}%`,
            }}
            animate={{ y: [0, -22, 0], opacity: [0.15, 0.6, 0.15] }}
            transition={{ duration: 5 + (i % 5), repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
          />
        ))}

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 sm:px-8 pt-28 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left content */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-8 backdrop-blur-sm"
            >
              <ShieldCheck className="h-4 w-4 text-[#00e5ff]" />
              <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-zinc-300 font-mono-wt">
                {t.hero.badge}
              </span>
            </motion.div>

            <h1 className="font-display font-light tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl xl:text-[4.2rem]">
              <MaskedLine delay={0.15}>
                <span className="text-white">{t.hero.title1}</span>
              </MaskedLine>
              <MaskedLine delay={0.32}>
                <span className="text-zinc-400">{t.hero.title2}</span>
              </MaskedLine>
              <MaskedLine delay={0.48}>
                <span className="bg-gradient-to-r from-[#00e5ff] to-[#0047ff] bg-clip-text text-transparent">
                  {t.hero.title3}
                </span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-7 max-w-xl text-base md:text-lg font-light leading-relaxed text-zinc-300"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
            >
              <a
                data-testid="hero-cta-primary"
                href={buildWhatsAppLink(t.hero.whatsappMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#0047ff] text-base font-semibold text-white hover:bg-[#2b5bff] transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_30px_rgba(0,71,255,0.55)]"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("servicos")}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/20 text-base font-medium text-white hover:border-white/50 hover:bg-white/5 transition-colors duration-300"
              >
                {t.hero.ctaSecondary}
                <ArrowDown className="h-5 w-5 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="mt-14 grid grid-cols-3 gap-4 sm:gap-8 max-w-xl border-t border-white/10 pt-8"
            >
              {stats.map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  {s.text ? (
                    <span className="font-display text-xl sm:text-3xl lg:text-4xl font-medium text-white">
                      {s.text}
                    </span>
                  ) : (
                    <CountUp
                      end={s.end}
                      prefix={s.prefix || ""}
                      suffix={s.suffix || ""}
                      className="font-display text-xl sm:text-3xl lg:text-4xl font-medium text-white"
                    />
                  )}
                  <div className="mt-1 text-xs sm:text-sm text-zinc-500">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: real screenshot product frame */}
          <motion.div
            style={{ y: frameY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-6 blue-glow opacity-80" />
              <div className="relative rounded-2xl border border-white/10 bg-[#0a0a0e]/80 p-2 shadow-[0_30px_80px_-20px_rgba(0,71,255,0.35)] backdrop-blur-sm">
                {/* window bar */}
                <div className="flex items-center gap-1.5 px-3 py-2.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                </div>
                <div className="overflow-hidden rounded-xl border border-white/5">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/hero-home.jpg`}
                    alt="Ambiente Waltech"
                    className="w-full object-cover"
                    loading="eager"
                  />
                </div>
              </div>

              {/* Floating cards */}
              <motion.div
                animate={reduce ? {} : { y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-6 top-1/4 flex items-center gap-3 rounded-xl border border-white/10 bg-[#0a0a0e]/90 px-4 py-3 backdrop-blur-md shadow-xl"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#00e5ff]/10">
                  <Lock className="h-4 w-4 text-[#00e5ff]" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-white">LGPD</div>
                  <div className="text-[11px] text-zinc-500">Dados protegidos</div>
                </div>
              </motion.div>

              <motion.div
                animate={reduce ? {} : { y: [0, 10, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="absolute -right-4 bottom-8 flex items-center gap-3 rounded-xl border border-white/10 bg-[#0a0a0e]/90 px-4 py-3 backdrop-blur-md shadow-xl"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0047ff]/15">
                  <Server className="h-4 w-4 text-[#4d7bff]" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-white">Uptime 24/7</div>
                  <div className="text-[11px] text-zinc-500">Monitoramento ativo</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
